import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useLocation, useHistory } from 'react-router-dom';
import { FiChevronLeft, FiMessageSquare, FiZap } from 'react-icons/fi';
import './Editor.css';
import { DataDiff, FestivalData, PUBLISHED_STATUS } from '../../model/FestivalData';
import InfosEditor from './components/InfosEditor/InfosEditor';
import NewsEditor from './components/NewsEditor/NewsEditor';
import ArtistsEditor from './components/ArtistsEditor/ArtistsEditor';
import PoisEditor from './components/PoisEditor/PoisEditor';
import ShowsEditor from './components/ShowsEditor/ShowsEditor';
import MapsEditor from './components/MapsEditor/MapsEditor';
import TicketsEditor from './components/TicketsEditor/TicketsEditor';
import { 
  uploadImage, 
  imageUrl, 
  fetchCurrentUser, 
} from '../../services/api';
import ConflictModal from '../../components/ConflictModal';
import { calculateDiff } from '../../utils/diffCalculator';
import { fetchCurrentVersion, fetchVersions } from '../../services/api';
import VersionHistory from './components/VersionHistory/VersionHistory';
import NotificationsEditor from './components/NotificationsEditor/NotificationsEditor';
import UserEditor from '../Admin/UserEditor/UserEditor';
import Cookies from 'js-cookie';
import { SESSION_COOKIE_ATTRIBUTES } from '../../services/sessionCookie';
import { Role, EditorType, getAccessibleEditors } from '../../model/Roles';
import { User } from '../../model/User';
import EditorMenu from './components/EditorMenu/EditorMenu';
import { FestivalDataService } from '../../services/festivalDataService';
import CarpoolsEditor from './components/CarpoolsEditor/CarpoolsEditor';
import ReportingEditor from './components/ReportingEditor/ReportingEditor';
import DeferrableSavingComponent from './components/DeferrableSavingComponent/DeferrableSavingComponent';
import SaveButtonsContainer from './components/SaveButtonsContainer/SaveButtonsContainer';
import PagesEditor from './components/PagesEditor/PagesEditor';
import AdvertisementsEditor from './components/AdvertisementsEditor/AdvertisementsEditor';
import PreviewPanel from './components/PreviewPanel/PreviewPanel';
import PublishDialog from './components/PublishDialog/PublishDialog';
import AgentChat from './components/AgentChat/AgentChat';
import { AgentSection, fetchFestivalAgentEnabled } from '../../services/festivalAgentService';
import { currentMessages, useMessages } from '../../i18n/I18n';
import { Messages } from '../../i18n/messages';
import LoadingPlaceholder, { Skeleton } from '../../components/LoadingPlaceholder';
import { prefetchTab } from '../../services/tabData';

const previewUrl = process.env.REACT_APP_PREVIEW_URL;

const copyOf = (data: FestivalData): FestivalData => JSON.parse(JSON.stringify(data));

/** What the API would refuse or silently replace, so the version can't be saved as is */
const findUnsavableData = (data: FestivalData, t: Messages<'editor'>): string | null => {
  const { background } = data.infos.stylesheet;
  // The API would store its default background instead: a white solid color
  if (background.type === 'Image' && !background.image) {
    return t.backgroundImageMissing;
  }
  return null;
};
// Each tab is its own route, so the Editor remounts on tab change: remember the panel across tabs
const PREVIEW_OPEN_STORAGE_KEY = 'previewPanelOpen';
// Tabs editing the version's data, saved together with the Save button
const VERSIONED_EDITORS: EditorType[] = ['infos', 'map', 'tickets', 'artists', 'pois', 'shows'];
// Tabs whose content the API saves as soon as it changes, for every version
const LIVE_EDITORS: EditorType[] = ['pages', 'news', 'notifications', 'users', 'carpools', 'reporting', 'advertisements'];

interface EditorProps {
  sessionToken: string;
  festivalId: string;
  currentUser: User;
  /** The tab in the URL. The editor stays mounted while it changes */
  tab: EditorType;
}

interface LocationState {
  versionData: FestivalData;
}

const Editor: React.FC<EditorProps> = ({ sessionToken, festivalId, currentUser, tab }) => {
  const { versionId } = useParams<{ versionId: string }>();
  const location = useLocation<LocationState>();
  const history = useHistory();
  const t = useMessages('editor');
  const common = useMessages('common');
  
  // Opened from the version list, the version comes with the navigation and shows at once
  const [isLoading, setIsLoading] = useState(() => !location.state?.versionData);
  const [festivalData, setFestivalData] = useState<FestivalData | null>(location.state?.versionData || null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeEditor, setActiveEditor] = useState<EditorType>(tab);
  const [unsavedChanges, setUnsavedChanges] = useState<Set<string>>(new Set());
  const originalDataRef = useRef<FestivalData | null>(null);
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
  /** The status the version is about to get, while the publish dialog asks */
  const [statusChange, setStatusChange] = useState<PUBLISHED_STATUS | null>(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictDiffs, setConflictDiffs] = useState<DataDiff[]>([]);
  const [pendingChanges, setPendingChanges] = useState<FestivalData | null>(null);
  const [currentUserData, setCurrentUserData] = useState<User>(currentUser);
  const allowedEditors = useMemo(() => getAccessibleEditors(currentUserData.roles), [currentUserData.roles]);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(() => localStorage.getItem(PREVIEW_OPEN_STORAGE_KEY) === 'true');
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  /** Bumped when a tab switch picked up a newer save from the server, so the open tab starts over from it */
  const [serverRevision, setServerRevision] = useState(0);
  const [isAgentEnabled, setIsAgentEnabled] = useState(false);
  const [isAgentOpen, setIsAgentOpen] = useState(false);
  const agentButtonRef = useRef<HTMLButtonElement>(null);
  const festivalDataService = useRef(new FestivalDataService(sessionToken, festivalId));

  /** Every version with its saved status, for the publish dialog to tell which one the apps show */
  const loadVersionStatuses = useCallback(
    async () => (await fetchVersions(sessionToken, festivalId)).map(({ version, status }) => ({ version, status })),
    [sessionToken, festivalId],
  );
  // The version as last read from or saved to the server; a save conflicts when the server no longer has it
  const serverDataRef = useRef<FestivalData | null>(location.state?.versionData ? copyOf(location.state.versionData) : null);
  // Lifts the unsaved-changes guard on navigation, once the user chose to save or discard
  const unblockNavigationRef = useRef<(() => void) | null>(null);

  const getApiImageUrl = useCallback((identifier: string) => {
    return festivalDataService.current.getImageUrl(identifier);
  }, []);

  const findChangedProperties = (originalObj: any, newObj: any, prefix = ''): string[] => {
    const changedProps: string[] = [];

    if (originalObj === null || newObj === null || typeof originalObj !== 'object' || typeof newObj !== 'object') {
      if (originalObj !== newObj) {
        changedProps.push(prefix);
      }
      return changedProps;
    }

    const allKeys = Array.from(new Set([...Object.keys(originalObj), ...Object.keys(newObj)]));

    for (const key of allKeys) {
      const newPrefix = prefix ? `${prefix}.${key}` : key;
      
      if (!(key in originalObj)) {
        changedProps.push(newPrefix);
      } else if (!(key in newObj)) {
        changedProps.push(newPrefix);
      } else if (Array.isArray(newObj[key])) {
        if (JSON.stringify(originalObj[key]) !== JSON.stringify(newObj[key])) {
          changedProps.push(newPrefix);
        }
      } else if (typeof newObj[key] === 'object' && newObj[key] !== null) {
        changedProps.push(...findChangedProperties(originalObj[key], newObj[key], newPrefix));
      } else if (originalObj[key] !== newObj[key]) {
        changedProps.push(newPrefix);
      }
    }

    return changedProps;
  };

  const handleDataChange = useCallback((key: keyof FestivalData, newData: any, changedFields?: string[]) => {
    setFestivalData(prevData => {
      if (!prevData) return null;
      return {
        ...prevData,
        [key]: newData
      } as FestivalData;
    });

    const originalData = originalDataRef.current;
    if (originalData) {
      if (changedFields) {
        setUnsavedChanges(prev => {
          const newSet = new Set(prev);
          changedFields.forEach(field => newSet.add(`${key}.${field}`));
          return newSet;
        });
      } else {
        const changedProperties = findChangedProperties(originalData[key], newData);
        setUnsavedChanges(prev => {
          // The section's changes as they are now: a field typed back to its saved value is no longer one
          const newSet = new Set(Array.from(prev).filter(change => !change.startsWith(`${key}.`)));
          changedProperties.forEach(prop => newSet.add(`${key}.${prop}`));
          return newSet;
        });
      }
    }
  }, []);

  useEffect(() => {
    const loadVersionData = async () => {
      if (!festivalData && versionId) {
        try {
          setIsLoading(true);
          const currentVersion = await festivalDataService.current.loadVersion(versionId);
          serverDataRef.current = copyOf(currentVersion);
          setFestivalData(currentVersion);
        } catch (error) {
          console.error('Error loading version:', error);
          setError(currentMessages('editor').loadFailed);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    loadVersionData();
  }, [versionId, festivalData]);

  // The data as loaded: edits are compared with it, so typing a field back to its saved value is no change.
  // Saving, reverting, reloading, refreshing and discarding set it again.
  useEffect(() => {
    if (festivalData && !originalDataRef.current) {
      originalDataRef.current = festivalData;
    }
  }, [festivalData]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (unsavedChanges.size > 0) {
        setShowUnsavedModal(true);
        setPendingNavigation(() => () => {
          window.location.reload();
        });
        
        e.preventDefault();
        e.returnValue = '';
        
        return '';
      }
    };

    const handleUnload = (e: Event) => {
      if (unsavedChanges.size > 0) {
        e.preventDefault();
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, [unsavedChanges]);

  useEffect(() => {
    const updateUserData = async () => {
      try {
        const updatedUser = await fetchCurrentUser(sessionToken, festivalId);
        const currentSession = JSON.parse(Cookies.get('session') || '{}');
        const updatedSession = {
          ...currentSession,
          user: updatedUser
        };
        Cookies.set('session', JSON.stringify(updatedSession), SESSION_COOKIE_ATTRIBUTES);
        setCurrentUserData(updatedUser);
      } catch (error) {
        console.error('Failed to update user data:', error);
      }
    };

    updateUserData();
  }, [sessionToken, festivalId]);

  // Back and forward between tabs change the URL only
  useEffect(() => {
    setActiveEditor(tab);
  }, [tab]);

  // A tab switch shows the version at once, then picks up a newer save by someone else, if nothing is unsaved
  const unsavedCountRef = useRef(0);
  unsavedCountRef.current = unsavedChanges.size;
  const isFirstTabRef = useRef(true);
  useEffect(() => {
    if (isFirstTabRef.current) {
      isFirstTabRef.current = false;
      return;
    }
    if (!serverDataRef.current || unsavedCountRef.current > 0) return;
    let isCurrent = true;
    festivalDataService.current.loadVersion(versionId)
      .then(latest => {
        if (!isCurrent || unsavedCountRef.current > 0 || latest.updated_at === serverDataRef.current?.updated_at) return;
        serverDataRef.current = copyOf(latest);
        originalDataRef.current = latest;
        setFestivalData(latest);
        setServerRevision(revision => revision + 1);
      })
      .catch(error => console.error('Failed to refresh the version:', error));
    return () => {
      isCurrent = false;
    };
  }, [activeEditor, versionId]);

  useEffect(() => {
    if (!allowedEditors.includes(activeEditor)) {
      setActiveEditor(allowedEditors[0]);
      // The address says which tab is shown
      history.replace(`/editor/${versionId}/${allowedEditors[0]}`);
    }
  }, [allowedEditors, activeEditor, history, versionId]);

  // The agent button stays hidden until the API has the assistant set up
  const isAdmin = currentUserData.roles.includes(Role.ADMIN);
  useEffect(() => {
    if (!isAdmin) return;
    let isCurrent = true;
    fetchFestivalAgentEnabled(sessionToken, festivalId).then(enabled => {
      if (isCurrent) setIsAgentEnabled(enabled);
    });
    return () => {
      isCurrent = false;
    };
  }, [isAdmin, sessionToken, festivalId]);

  useEffect(() => {
    const handleTabSwitch = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail === 'map') {
        setActiveEditor('map');
      }
    };

    window.addEventListener('switchTab', handleTabSwitch);
    return () => {
      window.removeEventListener('switchTab', handleTabSwitch);
    };
  }, []);

  useEffect(() => {
    if (unsavedChanges.size > 0) {
      const unblock = history.block((tx) => {
        setShowUnsavedModal(true);
        setPendingNavigation(() => () => {
          unblock();
          history.push(tx.pathname);
        });
        return false;
      });
      unblockNavigationRef.current = unblock;
      return () => {
        unblock();
        unblockNavigationRef.current = null;
      };
    }
  }, [unsavedChanges, history]);

  if (isLoading) {
    // The editor's frame at once, with the version's content as placeholders until it arrives
    return (
      <div className="editor">
        <header className="editor-header">
          <div className="header-content">
            <Link to="/" className="editor-back" aria-label={t.backToVersions}>
              <FiChevronLeft aria-hidden="true" />
              {t.versions}
            </Link>
            <Skeleton width={280} height={20} className="editor-title-placeholder" />
          </div>
        </header>
        <div className="editor-layout">
          <EditorMenu
            version={Number(versionId)}
            allowedEditors={allowedEditors}
            activeEditor={activeEditor}
            onTabChange={newTab => history.push(`/editor/${versionId}/${newTab}`)}
          />
          <main className="editor-main">
            <div className="editor-content">
              <LoadingPlaceholder layout={VERSIONED_EDITORS.includes(activeEditor) ? 'form' : 'rows'} />
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!festivalData) {
    return (
      <div className="editor">
        <div className="error">{t.loadFailed}</div>
        <Link to="/" className="editor-back" aria-label={t.backToVersions}>
          <FiChevronLeft aria-hidden="true" />
          {t.versions}
        </Link>
      </div>
    );
  }

  // The API saves the revert itself, so the reverted data is the new server state
  const handleRevert = (savedData: FestivalData) => {
    serverDataRef.current = copyOf(savedData);
    setFestivalData(savedData);
    originalDataRef.current = savedData;
    setUnsavedChanges(new Set());
    setLastSaveTime(new Date());
    setPreviewRefreshKey(key => key + 1);
  };

  const renderActiveEditor = () => {
    switch (activeEditor) {
      case 'infos':
        return (
          <DeferrableSavingComponent>
            <InfosEditor 
              infos={festivalData.infos} 
              onChange={(newInfos) => handleDataChange('infos', newInfos)}
              internalName={festivalData.internal_name}
              onInternalNameChange={(internalName) => handleDataChange('internal_name', internalName)}
              getApiImageUrl={getApiImageUrl}
            />
          </DeferrableSavingComponent>
        );
      case 'map':
        return (
          <DeferrableSavingComponent>
            <MapsEditor 
              maps={festivalData.maps}
              onChange={(newMaps) => handleDataChange('maps', newMaps)}
              getApiImageUrl={getApiImageUrl}
            />
          </DeferrableSavingComponent>
        );
      case 'tickets':
        return (
          <DeferrableSavingComponent>
            <TicketsEditor 
              tickets={festivalData.infos.tickets} 
              onChange={(newTickets) => handleDataChange('infos', { ...festivalData.infos, tickets: newTickets })}
              getApiImageUrl={getApiImageUrl}
            />
          </DeferrableSavingComponent>
        );
      case 'news':
        return (
            <NewsEditor
              sessionToken={sessionToken}
              festivalId={festivalId}
              getApiImageUrl={getApiImageUrl}
            />
        );
      case 'artists':
        return (
          <DeferrableSavingComponent>
            <ArtistsEditor
              artists={festivalData.artists}
              shows={festivalData.shows}
              pois={festivalData.pois}
              onChange={(newArtists) => handleDataChange('artists', newArtists)}
              getApiImageUrl={getApiImageUrl}
            />
          </DeferrableSavingComponent>
        );
      case 'pois':
        return (
          <DeferrableSavingComponent>
            <PoisEditor 
              pois={festivalData.pois} 
              onChange={(newPois) => handleDataChange('pois', newPois)}
              getApiImageUrl={getApiImageUrl}
              maps={festivalData.maps}
            />
          </DeferrableSavingComponent>
        );
      case 'shows':
        return (
          <DeferrableSavingComponent>
            <ShowsEditor
              shows={festivalData.shows}
              artists={festivalData.artists}
              pois={festivalData.pois}
              onChange={(newShows) => handleDataChange('shows', newShows)}
              getApiImageUrl={(identifier) => imageUrl(identifier, festivalId)}
            />
          </DeferrableSavingComponent>
        );
      case 'history':
        return (
          <VersionHistory
            sessionToken={sessionToken}
            festivalId={festivalId}
            versionId={festivalData.version.toString()}
            isPublished={festivalData.status === 'published'}
            onRevert={handleRevert}
          />
        );
      case 'notifications':
        return (
          <NotificationsEditor
            shows={festivalData.shows}
            artists={festivalData.artists}
            sessionToken={sessionToken}
            festivalId={festivalId}
          />
        );
      case 'users':
        return (
          <UserEditor
            sessionToken={sessionToken}
            festivalId={festivalId}
            currentUser={currentUserData}
          />
        );
      case 'carpools':
        return (
          <CarpoolsEditor
            sessionToken={sessionToken}
            festivalId={festivalId}
            userRoles={currentUserData.roles}
          />
        );
      case 'reporting':
        return (
          <ReportingEditor
            sessionToken={sessionToken}
            festivalId={festivalId}
          />
        );
      case 'pages':
        return (
          <PagesEditor
            sessionToken={sessionToken}
            festivalId={festivalId}
            getApiImageUrl={getApiImageUrl}
          />
        );
      case 'advertisements':
        return (
          <AdvertisementsEditor
            sessionToken={sessionToken}
            festivalId={festivalId}
            getApiImageUrl={getApiImageUrl}
          />
        );
      default:
        return null;
    }
  };

  /** Publishing, or going back to draft, saves the version with its new status in one step */
  const handleConfirmStatusChange = async () => {
    if (!statusChange) return;
    await handleSave({ ...festivalData, status: statusChange });
    // A conflict or an error shows its own message, and the version keeps its status
    setStatusChange(null);
  };

  /** Reads the version back once saved: the API may have normalized what it stored */
  const rememberServerVersion = async (version: number) => {
    serverDataRef.current = copyOf(await festivalDataService.current.loadVersion(version.toString()));
  };

  /** Resolves to whether the version was saved: a conflict or an error leaves it unsaved */
  const handleSave = async (data: FestivalData | null = festivalData): Promise<boolean> => {
    if (!data) return false;

    const problem = findUnsavableData(data, t);
    if (problem) {
      setError(problem);
      return false;
    }
    
    try {
      setIsSaving(true);
      setError(null);

      // Check for conflicts
      const conflicts = await festivalDataService.current.checkForConflicts(data, serverDataRef.current);
      if (conflicts) {
        setPendingChanges(data);
        setConflictDiffs(conflicts);
        setShowConflictModal(true);
        return false;
      }

      // Save the data
      const { updated_at } = await festivalDataService.current.saveVersion(data);
      await rememberServerVersion(data.version);
      
      // Update local state
      const updatedFestivalData = {
        ...data,
        updated_at,
      };
      
      setFestivalData(updatedFestivalData);
      setUnsavedChanges(new Set());
      originalDataRef.current = updatedFestivalData;
      setLastSaveTime(new Date());
      setPreviewRefreshKey(key => key + 1);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : t.saveFailed);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleOverwrite = async () => {
    if (!pendingChanges) return;
    try {
      setIsSaving(true);
      const { updated_at } = await festivalDataService.current.saveVersion(pendingChanges);
      await rememberServerVersion(pendingChanges.version);
      const savedFestivalData = {
        ...pendingChanges,
        updated_at,
      };
      setFestivalData(savedFestivalData);
      setUnsavedChanges(new Set());
      originalDataRef.current = savedFestivalData;
      setLastSaveTime(new Date());
      setShowConflictModal(false);
      setPendingChanges(null);
      setPreviewRefreshKey(key => key + 1);
    } catch (error) {
      console.error('Error overwriting festival data:', error);
      setError(t.errorSaving);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReload = async () => {
    try {
      const currentVersion = await fetchCurrentVersion(sessionToken, festivalId, festivalData!.version.toString());
      serverDataRef.current = copyOf(currentVersion);
      originalDataRef.current = currentVersion;
      setFestivalData(currentVersion);
      setShowConflictModal(false);
      setPendingChanges(null);
      setUnsavedChanges(new Set());
      setLastSaveTime(new Date());
    } catch (error) {
      console.error('Error reloading festival data:', error);
      setError(t.errorReloading);
    }
  };

  const handleTabChange = (newTab: EditorType) => {
    if (unsavedChanges.size > 0) {
      setShowUnsavedModal(true);
      // Discarding goes back to the version as saved (handleDiscardChanges)
      setPendingNavigation(() => () => {
        unblockNavigationRef.current?.();
        history.push(`/editor/${versionId}/${newTab}`);
      });
    } else {
      setActiveEditor(newTab);
      history.push(`/editor/${versionId}/${newTab}`);
    }
  };

  const togglePreview = () => {
    setIsAgentOpen(false);
    localStorage.setItem(PREVIEW_OPEN_STORAGE_KEY, String(!isPreviewOpen));
    setIsPreviewOpen(!isPreviewOpen);
  };

  const toggleAgent = () => {
    if (!isAgentOpen) {
      setIsPreviewOpen(false);
      localStorage.setItem(PREVIEW_OPEN_STORAGE_KEY, 'false');
    }
    setIsAgentOpen(!isAgentOpen);
  };

  const closeAgent = () => {
    setIsAgentOpen(false);
    agentButtonRef.current?.focus();
  };

  const applyAgentChanges = (nextData: FestivalData) => {
    if (nextData.infos !== festivalData.infos) handleDataChange('infos', nextData.infos);
    if (nextData.artists !== festivalData.artists) handleDataChange('artists', nextData.artists);
    if (nextData.shows !== festivalData.shows) handleDataChange('shows', nextData.shows);
  };

  const agentSection: AgentSection | null = activeEditor === 'infos' || activeEditor === 'artists' || activeEditor === 'shows'
    ? activeEditor
    : null;
  const canUseAgent = isAgentEnabled && isAdmin && agentSection !== null;

  const handleDiscardChanges = () => {
    // The editor stays mounted across tabs, so the discarded edits must go from its data too
    if (serverDataRef.current) {
      const savedData = copyOf(serverDataRef.current);
      originalDataRef.current = savedData;
      setFestivalData(savedData);
    }
    setUnsavedChanges(new Set());
    if (pendingNavigation) {
      pendingNavigation();
    }
    setShowUnsavedModal(false);
  };

  const handleSaveAndQuit = async () => {
    const saved = await handleSave();
    setShowUnsavedModal(false);
    // A conflict or an error keeps the user here, with their changes
    if (saved && pendingNavigation) {
      pendingNavigation();
    }
  };

  return (
    <div className="editor">
      <header className="editor-header">
        <div className="header-content">
          <Link to="/" className="editor-back" aria-label={t.backToVersions}>
            <FiChevronLeft aria-hidden="true" />
            {t.versions}
          </Link>
          <h1>{festivalData.internal_name?.trim() || festivalData.infos.festival_name} - Version {festivalData.version}</h1>
          <div className="editor-header-actions">
            {canUseAgent && (
              <span className="agent-toggle-container" title={festivalData.status !== 'draft' ? t.agentDraftRequired : undefined}>
                <button
                  ref={agentButtonRef}
                  type="button"
                  className={`agent-toggle${isAgentOpen ? ' active' : ''}`}
                  aria-expanded={isAgentOpen && festivalData.status === 'draft'}
                  aria-controls="festival-agent-chat"
                  title={festivalData.status !== 'draft' ? t.agentDraftRequired : undefined}
                  disabled={festivalData.status !== 'draft' || isSaving}
                  onClick={toggleAgent}
                >
                  <FiMessageSquare aria-hidden="true" />
                  {t.editWithAgent}
                </button>
              </span>
            )}
            {VERSIONED_EDITORS.includes(activeEditor) && (
              <SaveButtonsContainer
                unsavedChanges={unsavedChanges}
                lastSaveTime={lastSaveTime}
                isSaving={isSaving}
                onSave={() => handleSave()}
                status={festivalData.status}
                onPublish={() => setStatusChange('published')}
                onUnpublish={() => setStatusChange('draft')}
              />
            )}
            {LIVE_EDITORS.includes(activeEditor) && (
              <span className="live-content-note">
                <FiZap aria-hidden="true" />
                {t.savedInstantly}
              </span>
            )}
            {previewUrl && (
              <button
                type="button"
                className={`preview-toggle${isPreviewOpen ? ' active' : ''}`}
                onClick={togglePreview}
              >
                {isPreviewOpen ? t.hidePreview : t.preview}
              </button>
            )}
          </div>
        </div>
      </header>
      <div className="editor-layout">
        <EditorMenu
          version={festivalData.version}
          allowedEditors={allowedEditors}
          activeEditor={activeEditor}
          onTabChange={handleTabChange}
          onTabIntent={tabToOpen => prefetchTab(tabToOpen, { sessionToken, festivalId, versionId })}
        />
        <main className="editor-main">
          <div className="editor-content content-appear" key={`${activeEditor}-${serverRevision}`}>
            {renderActiveEditor()}
            {error && <div className="error">{error}</div>}
          </div>
        </main>
        {canUseAgent && agentSection && isAgentOpen && festivalData.status === 'draft' && (
          <AgentChat
            sessionToken={sessionToken}
            festivalId={festivalId}
            festivalData={festivalData}
            section={agentSection}
            disabled={isSaving}
            onApply={applyAgentChanges}
            onClose={closeAgent}
          />
        )}
        {previewUrl && isPreviewOpen && (
          <PreviewPanel
            previewUrl={previewUrl}
            festivalId={festivalId}
            version={festivalData.version}
            refreshKey={previewRefreshKey}
            hasUnsavedChanges={unsavedChanges.size > 0}
            onClose={togglePreview}
          />
        )}
      </div>
      {statusChange && (
        <PublishDialog
          version={festivalData.version}
          status={statusChange}
          unsavedChanges={unsavedChanges.size}
          isSaving={isSaving}
          loadVersions={loadVersionStatuses}
          onConfirm={handleConfirmStatusChange}
          onCancel={() => setStatusChange(null)}
        />
      )}
      {showConflictModal && (
        <ConflictModal
          diffs={conflictDiffs}
          onOverwrite={handleOverwrite}
          onReload={handleReload}
          onClose={() => setShowConflictModal(false)}
        />
      )}
      {showUnsavedModal && (
        <div className="unsaved-modal-overlay">
          <div className="unsaved-modal">
            <h2>{t.unsavedTitle}</h2>
            <p>{t.unsavedQuestion}</p>
            <div className="unsaved-modal-buttons">
              <button 
                className="cancel"
                onClick={() => setShowUnsavedModal(false)}
              >
                {common.cancel}
              </button>
              <button 
                className="discard"
                onClick={handleDiscardChanges}
              >
                {t.discardChanges}
              </button>
              <button 
                className="save"
                onClick={handleSaveAndQuit}
                disabled={isSaving}
              >
                {isSaving ? t.saving : t.saveAndQuit}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Editor;
